import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useLineQuoteUrl } from "lib/line";

const NAV_ITEMS = [
  { label: "หน้าหลัก", href: "/" },
  { label: "บัญชีและภาษี", href: "/services/accounting" },
  { label: "จดทะเบียนธุรกิจ", href: "/services/registration" },
  { label: "ค่าบริการ", href: "/pricing" },
  { label: "บทความ", href: "/blog" }
];

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lineUrl = useLineQuoteUrl();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  return (
    <header
      className={`sticky top-0 z-50 border-b transition duration-300 ${
        scrolled ? "border-slate-200 bg-white/95 shadow-[0_8px_30px_-24px_rgba(15,23,42,0.5)] backdrop-blur-xl" : "border-slate-100 bg-white"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 sm:px-8">
        <Link
          href="/"
          className="flex items-center gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-4"
        >
          <span className="relative flex items-center">
            <Image
              src="/KMCPwithtagline-rebg.png"
              alt="KMCP Accounting"
              width={190}
              height={55}
              className="h-10 w-auto sm:h-11"
              sizes="(min-width: 640px) 180px, 140px"
              quality={90}
              priority
            />
          </span>
          <span className="sr-only">KMCP Accounting</span>
        </Link>
        <button
          className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full border border-slate-300 text-gray-800 transition hover:border-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 lg:hidden"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          aria-label={isMenuOpen ? "ปิดเมนู" : "เปิดเมนู"}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 5.25h16.5M3.75 12h16.5M12 18.75h8.25"
            />
          </svg>
        </button>
        <div className="hidden items-center gap-9 lg:flex">
          <div className="flex items-center gap-7 text-sm font-medium text-slate-600 xl:gap-9">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="relative rounded-sm py-2 transition after:absolute after:inset-x-0 after:bottom-0 after:h-px after:origin-left after:scale-x-0 after:bg-brand after:transition-transform hover:text-gray-950 hover:after:scale-x-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-4"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <a href={lineUrl} target="_blank" rel="noreferrer" className="btn-primary min-h-11 whitespace-nowrap px-5">
            สอบถามค่าบริการ
          </a>
        </div>
      </nav>
      {isMenuOpen && (
        <div id="mobile-navigation" className="lg:hidden">
          <div className="mx-4 mb-4 flex flex-col gap-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="min-h-11 rounded-xl px-3 py-3 text-base font-semibold text-gray-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <a
              href={lineUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary min-h-12"
              onClick={() => setIsMenuOpen(false)}
            >
              สอบถามค่าบริการ
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
